// format.ts — How a mass or a duration is written, everywhere on the site.
//
// Masses are summed from ingredient rows as floats, so a total arrives as
// 535.4000000000001 and not 535.4. Rounding is therefore not decoration: any
// surface that prints a mass has to go through here, or binary float noise
// leaks into the page.

// Scaling almost never lands on a whole gram, and one fixed precision cannot
// serve both ends of a recipe: a tenth is noise on 1560 g of dough and a lie on
// 0.4 g of yeast, which would round away to `0 g` and read as "leave it out".
// So the precision follows the magnitude, and trailing zeros are dropped.
export function formatGrams(massG: number): string {
    if (massG <= 0) return '0'
    const decimals = massG >= 100 ? 0 : massG >= 10 ? 1 : massG >= 1 ? 2 : 3
    return String(Number(massG.toFixed(decimals)))
}

// What a settled mass field shows: the mass, in grams, with its unit.
export function gramsLabel(massG: number): string {
    return `${formatGrams(massG)} g`
}

// A recipe's total, for chrome that has one line to say how much this makes.
// Past a kilogram the gram is below the noise floor of the number — nobody
// weighs a 2.4 kg batch to the gram — so it switches units rather than print
// four significant digits of false precision into a card.
export function formatMass(massG: number): string {
    if (massG >= 1000) return `${Number((massG / 1000).toFixed(2))} kg`
    return gramsLabel(massG)
}

// Durations come from the recipe as whole minutes.
export function formatMinutes(totalTimeMin: number): string {
    if (totalTimeMin < 60) return `${totalTimeMin}m`
    const m = totalTimeMin % 60
    return m > 0 ? `${Math.floor(totalTimeMin / 60)}h ${m}m` : `${Math.floor(totalTimeMin / 60)}h`
}
